import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Mail, Pencil, Phone } from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { StatusBadge } from '@/components/shared/status-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { fmtDate, fmtDateTime, maskPhone, money } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import type { CustomerDetail, CustomerHistory } from './api';
import { CustomerFormModal } from './customer-form';
import { CustomerLoyaltyTab } from './customer-loyalty';
import { CustomerSubscriptionsTab } from './customer-subscriptions';

const TABS = ['historico', 'fidelidade', 'assinaturas', 'dados'] as const;
const METHOD: Record<string, string> = { cash: 'Dinheiro', pix: 'Pix', credit: 'Crédito', debit: 'Débito', credit_card: 'Crédito', debit_card: 'Débito', other: 'Outro' };

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-display mt-0.5 truncate text-3xl leading-none tabular">{value}</dd>
    </div>
  );
}

export function CustomerDetailPage() {
  const { id = '' } = useParams();
  const tz = useTimezone();
  const { can } = useSession();
  const [tab, setTab] = useQueryState('aba', parseAsStringLiteral(TABS).withDefault('historico').withOptions({ history: 'replace' }));
  const [editOpen, setEditOpen] = useState(false);

  const customer = useQuery({ queryKey: ['customers', 'detail', id], queryFn: () => api.get<CustomerDetail>(`/customers/${id}`) });
  const history = useQuery({ queryKey: ['customers', 'history', id], queryFn: () => api.get<CustomerHistory>(`/customers/${id}/history`) });

  if (customer.isLoading) return <LoadingState label="Carregando cliente…" />;
  if (customer.error || !customer.data) {
    return (
      <div className="grid gap-4">
        <ErrorState error={customer.error} onRetry={() => customer.refetch()} />
        <Link to="/app/clientes" className="text-sm underline">
          Voltar para clientes
        </Link>
      </div>
    );
  }
  const c = customer.data;
  const t = history.data?.totals;

  return (
    <div>
      <Link to="/app/clientes" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden /> Clientes
      </Link>
      <div className="flex flex-col gap-3 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="page-title text-foreground">{c.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {c.phone && (
              <span className="inline-flex items-center gap-1.5 tabular">
                <Phone className="size-3.5" aria-hidden /> {maskPhone(c.phone)}
              </span>
            )}
            {c.email && (
              <span className="inline-flex items-center gap-1.5">
                <Mail className="size-3.5" aria-hidden /> {c.email}
              </span>
            )}
            <Badge variant={c.marketingConsent ? 'secondary' : 'outline'}>{c.marketingConsent ? 'Aceita comunicações' : 'Não aceita comunicações'}</Badge>
            {c.deletedAt && <Badge variant="outline">Excluído</Badge>}
          </div>
        </div>
        {can('customers.write') && !c.deletedAt && (
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil aria-hidden /> Editar cliente
          </Button>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 border-y py-5 md:grid-cols-5">
        <Stat label="Visitas concluídas" value={t ? String(t.completedVisits) : '—'} />
        <Stat label="Total gasto" value={t ? money(t.totalSpentCents) : '—'} />
        <Stat label="Ticket médio" value={t ? money(t.averageTicketCents) : '—'} />
        <Stat label="Última visita" value={t?.lastVisitAt ? fmtDate(t.lastVisitAt, tz) : history.isLoading ? '…' : 'Nunca'} />
        <Stat label="Barbeiro preferido" value={c.preferredBarberName ?? t?.topBarberName ?? '—'} />
      </dl>

      <Tabs value={tab} onValueChange={(v) => setTab(v as (typeof TABS)[number])} className="mt-6">
        <TabsList>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
          <TabsTrigger value="fidelidade">Fidelidade</TabsTrigger>
          <TabsTrigger value="assinaturas">Assinaturas e pacotes</TabsTrigger>
          <TabsTrigger value="dados">Dados</TabsTrigger>
        </TabsList>

        <TabsContent value="historico" className="mt-4 grid gap-6">
          {history.isLoading && <LoadingState />}
          {history.error && <ErrorState error={history.error} onRetry={() => history.refetch()} />}
          {history.data && (
            <>
              <section aria-labelledby="h-appts">
                <h2 id="h-appts" className="mb-2 text-sm font-semibold">
                  Atendimentos
                </h2>
                {history.data.appointments.length === 0 ? (
                  <EmptyState title="Nenhum atendimento ainda" description="Os agendamentos deste cliente aparecem aqui." />
                ) : (
                  <ul className="divide-y rounded-lg border bg-card">
                    {history.data.appointments.map((a) => (
                      <li key={a.id} className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 px-4 py-3 text-sm md:grid-cols-[9rem_1fr_auto_6rem]">
                        <span className="tabular text-muted-foreground">{fmtDateTime(a.startsAt, tz)}</span>
                        <span className="tabular text-right md:order-last">{money(a.paidCents ?? a.priceCents)}</span>
                        <span className="min-w-0 font-medium">
                          {a.serviceName} <span className="font-normal text-muted-foreground">com {a.barberName}</span>
                        </span>
                        <StatusBadge status={a.status} className="justify-self-end" />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section aria-labelledby="h-pay">
                <h2 id="h-pay" className="mb-2 text-sm font-semibold">
                  Pagamentos
                </h2>
                {history.data.payments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum pagamento registrado para este cliente.</p>
                ) : (
                  <ul className="divide-y rounded-lg border bg-card">
                    {history.data.payments.map((p) => (
                      <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                        <span className="tabular w-36 text-muted-foreground">{fmtDateTime(p.createdAt, tz)}</span>
                        <span className="min-w-0 flex-1 text-muted-foreground">
                          {p.methods.map((m) => METHOD[m.kind] ?? m.kind).join(' + ') || '—'}
                          {p.discountCents > 0 && ` · desconto ${money(p.discountCents)}`}
                        </span>
                        {p.status === 'refunded' && <Badge variant="outline">Estornado</Badge>}
                        <span className="tabular w-24 text-right font-medium">{money(p.totalCents)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </TabsContent>

        <TabsContent value="fidelidade" className="mt-4">
          <CustomerLoyaltyTab customerId={id} />
        </TabsContent>

        <TabsContent value="assinaturas" className="mt-4">
          <CustomerSubscriptionsTab customerId={id} disabled={!!c.deletedAt} />
        </TabsContent>

        <TabsContent value="dados" className="mt-4">
          <dl className="grid max-w-2xl gap-x-8 gap-y-4 text-sm sm:grid-cols-2">
            {[
              ['Nascimento', c.birthDate ? c.birthDate.split('-').reverse().join('/') : null],
              ['Etiquetas', c.tags.length ? c.tags.join(', ') : null],
              ['Preferências', c.preferences],
              ['Observações internas', c.notes],
              ['Cliente desde', fmtDate(c.createdAt, tz)],
              ['Aceite de comunicações', c.marketingConsentAt ? `Registrado em ${fmtDateTime(c.marketingConsentAt, tz)}` : null],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs text-muted-foreground">{k}</dt>
                <dd className="mt-0.5 whitespace-pre-wrap">{v ?? '—'}</dd>
              </div>
            ))}
          </dl>
        </TabsContent>
      </Tabs>
      <CustomerFormModal open={editOpen} onOpenChange={setEditOpen} customer={c} />
    </div>
  );
}
